<script setup>
import PageSpeedActionsCell from "@frontJs/components/tables/cells/projectManagement/dev/tenantReports/PageSpeedActionsCell.vue";
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/vue/24/solid";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route('front.projectManagement.dev.pageSpeed', {
            tenant: props.row.original.id,
          }),
          inertia: true
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "urlCount",
      header: () => t("label.urlCount"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.urlCount,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "averageDesktopScore",
      header: () => t("label.desktopAverageScore"),
      cell: (props) =>
        props.row.original.averageDesktopScore
          ? h(PillCell, {
              pillValue: props.row.original.averageDesktopScore,
              type:
                props.row.original.averageDesktopScore >= 90
                  ? "success"
                  : props.row.original.averageDesktopScore >= 50
                    ? "warning"
                    : "danger",
              leftIcon: ComputerDesktopIcon,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "averageMobileScore",
      header: () => t("label.mobileAverageScore"),
      cell: (props) =>
        props.row.original.averageMobileScore
          ? h(PillCell, {
              pillValue: props.row.original.averageMobileScore,
              type:
                props.row.original.averageMobileScore >= 90
                  ? "success"
                  : props.row.original.averageMobileScore >= 50
                    ? "warning"
                    : "danger",
              leftIcon: DevicePhoneMobileIcon,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "maxCreatedAt",
      header: () => t("label.lastCheckedAt"),
      cell: (props) =>
        props.row.original.maxCreatedAt
          ? h(TextCellV2, {
              text: formatDate(props.row.original.maxCreatedAt, false, true),
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(PageSpeedActionsCell, {
          row: innerProps.row,
        }),
    }),
  ],
  url: route("front.projectManagement.dev.postPageSpeed"),
  columnPinning: {
    right: ["actions"],
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    />
  </div>
</template>
